import React, { useState } from 'react';
import {
  Modal,
  Typography,
  Input,
  Form,
  Button,
  PhoneNumber,
  Textarea,
} from '@crisellevaldez/speculo-ui';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { teamMemberSchema, type TeamMemberInput } from '../../../schemas/teamMember.schema';
import { useTeamMemberPhoto } from '../../../hooks/organizations/useTeamMemberPhoto';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { Upload } from 'lucide-react';

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TeamMemberInput) => Promise<void>;
  isSubmitting?: boolean;
}

// Inner form component that resets when key changes
const TeamMemberForm: React.FC<{
  onSubmit: (data: TeamMemberInput) => Promise<void>;
  isSubmitting?: boolean;
}> = ({ onSubmit, isSubmitting }) => {
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { uploadPhoto } = useTeamMemberPhoto();
  const { organization } = useOrganizationStore();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TeamMemberInput>({
    resolver: zodResolver(teamMemberSchema),
    mode: 'onChange', // Validate on change to clear errors immediately
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      bio: '',
      slug: '',
      photoUrl: '',
    },
  });

  const onFormSubmit = async (data: TeamMemberInput) => {
    try {
      setFormError(null);
      let finalPhotoUrl = data.photoUrl || '';

      // Upload photo if selected
      if (selectedPhoto && organization?.id) {
        setIsUploadingPhoto(true);
        try {
          const tempId = `new_${Date.now()}`;
          finalPhotoUrl = await uploadPhoto(selectedPhoto, organization.id, tempId);
        } catch (error) {
          console.error('Error uploading photo:', error);
          setFormError('Failed to upload photo');
          setIsUploadingPhoto(false);
          return;
        }
        setIsUploadingPhoto(false);
      }

      await onSubmit({ ...data, photoUrl: finalPhotoUrl });

      // Reset form only after successful submit
      reset();
      setSelectedPhoto(null);
      setFormError(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error instanceof Error) {
        setFormError(error.message);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} data-tour="team-member-form">
      <div className="space-y-4">
        {/* Form error message */}
        {formError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-md">
            <p className="text-red-600 dark:text-red-400 text-sm">{formError}</p>
          </div>
        )}

        <div data-tour="team-member-required-fields" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Control data-tour="team-member-firstname">
            <Form.Label required>First name</Form.Label>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="Enter first name"
                  error={errors.firstName?.message}
                />
              )}
            />
          </Form.Control>

          <Form.Control data-tour="team-member-lastname">
            <Form.Label>Last name</Form.Label>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value || ''}
                  placeholder="Enter last name"
                  error={errors.lastName?.message}
                />
              )}
            />
          </Form.Control>
        </div>

        {/* Email and Phone - side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Control data-tour="team-member-email">
            <Form.Label required>Email</Form.Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="email"
                  placeholder="you@example.com"
                  error={errors.email?.message}
                />
              )}
            />
          </Form.Control>

          <Form.Control data-tour="team-member-phone">
            <Form.Label required>Phone number</Form.Label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneNumber
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.phoneNumber?.message}
                />
              )}
            />
          </Form.Control>
        </div>

        <Form.Control>
          <Form.Label>Bio</Form.Label>
          <Controller
            name="bio"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value || ''}
                placeholder="Enter team member bio"
                error={errors.bio?.message}
                rows={4}
              />
            )}
          />
        </Form.Control>

        <Form.Control>
          <Form.Label>URL Slug</Form.Label>
          <Controller
            name="slug"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value || ''}
                placeholder="Enter custom URL slug (optional)"
                error={errors.slug?.message}
                helperText="Custom URL for team member profile page. Use lowercase letters, numbers, and hyphens only."
              />
            )}
          />
        </Form.Control>

        <Form.Control>
          <Form.Label>Photo</Form.Label>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('photo-upload')?.click()}
                disabled={isUploadingPhoto}
              >
                <Upload className="w-4 h-4 mr-2" />
                Browse
              </Button>
              {selectedPhoto && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedPhoto.name} ({Math.round(selectedPhoto.size / 1024)} KB)
                </span>
              )}
            </div>
            {selectedPhoto && (
              <div className="flex justify-start">
                <div className="w-24 h-24 rounded-md overflow-hidden bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                  <img
                    src={URL.createObjectURL(selectedPhoto)}
                    alt="Team member photo preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
            <input
              id="photo-upload"
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(e) => setSelectedPhoto(e.target.files?.[0] || null)}
            />
          </div>
        </Form.Control>

        <div className="flex justify-end w-full mt-6">
          <Button
            type="submit"
            disabled={isUploadingPhoto}
            isLoading={isSubmitting || isUploadingPhoto}
            data-tour="team-member-submit"
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
};

export const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  // Key changes when modal opens to reset the form state
  const [formKey, setFormKey] = useState(0);

  const handleClose = () => {
    setFormKey((prev) => prev + 1); // Reset form on close
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>
        <Typography variant="h3" className="font-bold">
          Add New Team Member
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <TeamMemberForm key={formKey} onSubmit={onSubmit} isSubmitting={isSubmitting} />
      </Modal.Body>
    </Modal>
  );
};
